import { useState, useEffect, useCallback } from 'react';
import GlassCard from '../components/GlassCard';
import CrewMap from '../components/CrewMap';
import Badge from '../components/Badge';
import { apiService } from '../services/api';

interface NetworkData {
  airports: Array<{
    code: string;
    name: string;
    city: string;
    lat: number;
    lng: number;
    flight_count: number;
  }>;
  routes: Array<{
    from: string;
    to: string;
    flight_count: number;
    from_coords: [number, number];
    to_coords: [number, number];
  }>;
  total_routes: number;
  total_airports: number;
}

interface CrewMember {
  id: number;
  employee_id: string;
  first_name: string;
  last_name: string;
  position: string;
  rank: string;
  base: string;
  status: string;
}

const NetworkMap = () => {
  const [networkData, setNetworkData] = useState<NetworkData | null>(null);
  const [crewList, setCrewList] = useState<CrewMember[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [crewDestinations, setCrewDestinations] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [viewMode, setViewMode] = useState<'network' | 'crew'>('network');

  const fetchNetworkData = useCallback(async () => {
    try {
      const data = await apiService.getNetworkMap();
      setNetworkData(data);
    } catch (err) {
      console.error('Failed to fetch network data:', err);
    }
  }, []);

  const fetchCrewList = useCallback(async () => {
    try {
      const response = await apiService.getCrewMembers({ status: 'active', per_page: 100 });
      setCrewList(response.data || []);
    } catch (err) {
      console.error('Failed to fetch crew list:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchNetworkData(), fetchCrewList()]);
      setLoading(false);
    };
    loadData();
  }, [fetchNetworkData, fetchCrewList]);

  const handleCrewSelect = async (crew: CrewMember) => {
    setSelectedCrew(crew);
    setViewMode('crew');
    setLoadingDestinations(true);

    try {
      const destinations = await apiService.getCrewDestinations(crew.id);
      setCrewDestinations(destinations);
    } catch (err) {
      console.error('Failed to fetch crew destinations:', err);
      setCrewDestinations(null);
    } finally {
      setLoadingDestinations(false);
    }
  };

  const handleBackToNetwork = () => {
    setViewMode('network');
    setSelectedCrew(null);
    setCrewDestinations(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-slate-400">
          <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading map data...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            {viewMode === 'crew' && selectedCrew ? (
              <>
                <button
                  onClick={handleBackToNetwork}
                  className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                {selectedCrew.first_name} {selectedCrew.last_name}'s Routes
              </>
            ) : (
              'Route Network'
            )}
          </h1>
          <p className="text-slate-400 mt-1">
            {viewMode === 'network'
              ? 'FlightAtom Airways route network visualization'
              : `Viewing destinations for ${selectedCrew?.first_name} ${selectedCrew?.last_name}`}
          </p>
        </div>

        {networkData && viewMode === 'network' && (
          <div className="flex gap-3">
            <Badge variant="info">{networkData.total_airports} Airports</Badge>
            <Badge variant="success">{networkData.total_routes} Routes</Badge>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          {viewMode === 'network' && networkData ? (
            <CrewMap
              airports={networkData.airports}
              routes={networkData.routes}
              showRoutes={true}
              height="600px"
            />
          ) : loadingDestinations ? (
            <GlassCard className="flex items-center justify-center" style={{ height: '600px' }}>
              <div className="flex items-center gap-3 text-slate-400">
                <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading destinations...
              </div>
            </GlassCard>
          ) : crewDestinations ? (
            <CrewMap
              crewId={selectedCrew?.id}
              crewName={`${selectedCrew?.first_name} ${selectedCrew?.last_name}`}
              destinations={crewDestinations.destinations}
              showRoutes={false}
              height="600px"
            />
          ) : (
            <GlassCard className="flex items-center justify-center" style={{ height: '600px' }}>
              <div className="text-slate-400">No destination data available</div>
            </GlassCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* View Toggle */}
          <GlassCard className="p-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleBackToNetwork()}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'network'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Network View
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'crew'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                disabled={!selectedCrew}
              >
                Crew View
              </button>
            </div>
          </GlassCard>

          {/* Crew Selection */}
          <GlassCard className="p-4">
            <h3 className="text-sm font-medium text-white mb-3">Select Crew Member</h3>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {crewList.map((crew) => (
                <button
                  key={crew.id}
                  onClick={() => handleCrewSelect(crew)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                    selectedCrew?.id === crew.id
                      ? 'bg-blue-600/20 border border-blue-500'
                      : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                    {crew.first_name[0]}{crew.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {crew.first_name} {crew.last_name}
                    </div>
                    <div className="text-xs text-slate-400">
                      {crew.rank} • {crew.base}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}

              {crewList.length === 0 && (
                <div className="text-center text-slate-400 py-4">
                  No crew members found
                </div>
              )}
            </div>
          </GlassCard>

          {/* Crew Details */}
          {selectedCrew && (
            <GlassCard className="p-4">
              <h3 className="text-sm font-medium text-white mb-3">Crew Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Employee ID</span>
                  <span className="text-white">{selectedCrew.employee_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Position</span>
                  <span className="text-white">{selectedCrew.position}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Rank</span>
                  <span className="text-white">{selectedCrew.rank}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Base</span>
                  <Badge>{selectedCrew.base}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Status</span>
                  <Badge variant={selectedCrew.status === 'active' ? 'success' : 'warning'}>
                    {selectedCrew.status}
                  </Badge>
                </div>
              </div>

              {crewDestinations && crewDestinations.destinations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="text-sm font-medium text-white mb-2">
                    Recent Destinations ({crewDestinations.destinations.length})
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {crewDestinations.destinations.slice(0, 5).map((dest: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <span className="text-blue-400">{dest.departure.code}</span>
                        <span className="text-slate-500">→</span>
                        <span className="text-green-400">{dest.arrival.code}</span>
                        <span className="text-slate-500 ml-auto">{dest.flight_number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkMap;
